export type PriceBox = { x: number; y: number; w: number; h: number };

const IMAGE_NAME = /\.(png|jpe?g|gif|webp|bmp)$/i;
const PDF_NAME = /\.pdf$/i;
const UNSUPPORTED_IMAGE = /(tiff|tif|heic|heif)/i;

export function isPdfOaFile(name: string, contentType?: string | null): boolean {
  const type = String(contentType || '').toLowerCase();
  return type.includes('pdf') || PDF_NAME.test(name);
}

export function isRedactableOaFile(name: string, contentType?: string | null): boolean {
  const type = String(contentType || '').toLowerCase();
  if (isPdfOaFile(name, type)) return true;
  if (UNSUPPORTED_IMAGE.test(type) || UNSUPPORTED_IMAGE.test(name)) return false;
  if (type.startsWith('image/')) return true;
  return IMAGE_NAME.test(name);
}

export function redactedFileName(name: string): string {
  const trimmed = String(name || '').trim() || 'file';
  const dot = trimmed.lastIndexOf('.');
  const hasExt = dot > 0 && /^[A-Za-z0-9]+$/.test(trimmed.slice(dot + 1));
  const stem = (hasExt ? trimmed.slice(0, dot) : trimmed).replace(/-redacted$/i, '');
  const ext = hasExt ? trimmed.slice(dot) : '';
  return `${stem}-redacted${ext}`;
}

function asNumber(value: unknown): number | null {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

export type PdfTextRect = {
  str: string;
  x: number;
  y: number;
  w: number;
  h: number;
};

export function clampPriceBox(raw: Partial<PriceBox> & { width?: number; height?: number }): PriceBox | null {
  const x = asNumber(raw.x);
  const y = asNumber(raw.y);
  const w = asNumber(raw.w ?? raw.width);
  const h = asNumber(raw.h ?? raw.height);
  if (x == null || y == null || w == null || h == null) return null;
  const scale = [x, y, w, h].some((part) => part > 1.05) ? 0.01 : 1;
  const nx = Math.min(1, Math.max(0, x * scale));
  const ny = Math.min(1, Math.max(0, y * scale));
  const nw = Math.min(1 - nx, Math.max(0, w * scale));
  const nh = Math.min(1 - ny, Math.max(0, h * scale));
  if (nw < 0.002 || nh < 0.002) return null;
  return { x: nx, y: ny, w: nw, h: nh };
}

/** Pixel text rects are already on the page. Never treat them as 0–100 vision boxes. */
export function pixelRectToUnitBox(
  rect: Pick<PdfTextRect, 'x' | 'y' | 'w' | 'h'>,
  pageWidth: number,
  pageHeight: number,
): PriceBox | null {
  if (pageWidth <= 0 || pageHeight <= 0 || rect.w <= 0 || rect.h <= 0) return null;
  const x = Math.min(1, Math.max(0, rect.x / pageWidth));
  const y = Math.min(1, Math.max(0, rect.y / pageHeight));
  const w = Math.min(1 - x, Math.max(0, rect.w / pageWidth));
  const h = Math.min(1 - y, Math.max(0, rect.h / pageHeight));
  if (w < 0.002 || h < 0.002) return null;
  return { x, y, w, h };
}

export function padPriceBox(box: PriceBox, pad = 0.012): PriceBox {
  const x = Math.max(0, box.x - pad);
  const y = Math.max(0, box.y - pad);
  return {
    x,
    y,
    w: Math.min(1 - x, box.w + pad * 2),
    h: Math.min(1 - y, box.h + pad * 2),
  };
}

export function parsePriceBoxes(raw: unknown): PriceBox[] {
  const rows = Array.isArray(raw) ? raw : (raw && typeof raw === 'object' ? (raw as { boxes?: unknown }).boxes : null);
  if (!Array.isArray(rows)) return [];
  return rows
    .map((row) => (row && typeof row === 'object' ? clampPriceBox(row as PriceBox) : null))
    .filter((box): box is PriceBox => !!box)
    .map((box) => padPriceBox(box));
}

const CURRENCY_MARK = /[£€$]|gbp/i;
const POUND_ONLY = /^\s*[£€$]\s*$/;

function tidyText(text: string): string {
  return String(text || '').replace(/\u00a0/g, ' ');
}

export function isPriceSnippet(text: string): boolean {
  const value = tidyText(text);
  return CURRENCY_MARK.test(value) && /\d/.test(value);
}

export function isBareMoneyAmount(text: string): boolean {
  return /^\s*\d{1,3}(?:[,\s]\d{3})*\.\d{2}\s*$/.test(tidyText(text));
}

export function lineHasPriceCue(text: string): boolean {
  return /[£€$]|gbp|\b(net|nett|vat|sub[\s-]?total|total|price|extra|items|discount|amount|cost)\b/i.test(tidyText(text));
}

function groupTextRectsByLine(rects: PdfTextRect[]): PdfTextRect[][] {
  const sorted = [...rects].sort((a, b) => a.y - b.y || a.x - b.x);
  const lines: PdfTextRect[][] = [];
  sorted.forEach((rect) => {
    const mid = rect.y + rect.h / 2;
    const line = lines.find((row) => {
      const sample = row[0];
      const sampleMid = sample.y + sample.h / 2;
      return Math.abs(mid - sampleMid) <= Math.max(6, Math.max(rect.h, sample.h) * 0.75);
    });
    if (line) line.push(rect);
    else lines.push([rect]);
  });
  lines.forEach((line) => line.sort((a, b) => a.x - b.x));
  return lines;
}

function nextVisibleRect(line: PdfTextRect[], index: number): PdfTextRect | null {
  for (let i = index + 1; i < line.length; i += 1) {
    if (line[i].str.trim()) return line[i];
  }
  return null;
}

/** Cover the amount and the currency mark when PDF text is split across items. */
export function moneyBoxesFromTextRects(
  rects: PdfTextRect[],
  pageWidth: number,
  pageHeight: number,
): PriceBox[] {
  const marked = new Set<PdfTextRect>();
  groupTextRectsByLine(rects).forEach((line) => {
    const cue = lineHasPriceCue(line.map((rect) => rect.str).join(' '));
    line.forEach((rect, index) => {
      const next = nextVisibleRect(line, index);
      if (isPriceSnippet(rect.str)) marked.add(rect);
      if (POUND_ONLY.test(rect.str)) {
        marked.add(rect);
        if (next && (isBareMoneyAmount(next.str) || /\d/.test(next.str))) marked.add(next);
      }
      if (cue && isBareMoneyAmount(rect.str)) marked.add(rect);
    });
  });
  return [...marked]
    .map((rect) => pixelRectToUnitBox(rect, pageWidth, pageHeight))
    .filter((box): box is PriceBox => !!box)
    .map((box) => padPriceBox(box, 0.006));
}

export function paintPriceBoxes(
  ctx: Pick<CanvasRenderingContext2D, 'fillRect' | 'fillStyle'>,
  width: number,
  height: number,
  boxes: PriceBox[],
) {
  ctx.fillStyle = '#000';
  boxes.forEach((box) => {
    ctx.fillRect(box.x * width, box.y * height, box.w * width, box.h * height);
  });
}

export function jobCardOaDisplayName(redactedName: string | null | undefined, originalName: string): string {
  return String(redactedName || '').trim() || redactedFileName(originalName);
}
